import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import {
  useBulkUploadItems,
  useGetBulkUploadStatus,
  useDownloadBulkUploadTemplate,
} from '../../Query/StoreItemQuery';
import { BulkUploadItemAddedByEnum } from '../../../client-sdk';

interface BulkUploadModalProps {
  open: boolean;
  onClose: () => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadJobId, setUploadJobId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bulkUploadMutation = useBulkUploadItems();
  const downloadTemplateMutation = useDownloadBulkUploadTemplate();

  // Get user data to determine addedBy
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const addedBy: BulkUploadItemAddedByEnum =
    userData?.userDetails?.userType === 'Admin'
      ? BulkUploadItemAddedByEnum.Admin
      : BulkUploadItemAddedByEnum.Merchant;
  const merchantId = userData?.userId;

  // Poll for status if we have a jobId
  const { data: statusData, isLoading: isStatusLoading } = useGetBulkUploadStatus({
    jobId: uploadJobId || '',
    enabled: !!uploadJobId && open,
    refetchInterval: 2000,
  });

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setUploadJobId(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [open]);

  // Common file validation/selection logic
  const handleFileSelect = (file: File | null | undefined) => {
    if (!file) return;

    const validExtensions = ['.xlsx', '.csv'];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf('.'))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      alert(t('storeItem.bulkUpload.invalidFileType'));
      return;
    }

    setSelectedFile(file);
    setUploadJobId(null);
  };

  // Handle file selection from input
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    handleFileSelect(file);
  };

  // Drag & drop handlers
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragOver) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const file = event.dataTransfer.files?.[0];
    handleFileSelect(file);
  };

  // Handle file upload
  const handleUpload = () => {
    if (!selectedFile) {
      alert(t('storeItem.bulkUpload.noFileSelected'));
      return;
    }

    // Validate file size (e.g., max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (selectedFile.size > maxSize) {
      alert(t('storeItem.bulkUpload.fileTooLarge') || 'File size exceeds 10MB limit');
      return;
    }

    bulkUploadMutation.mutate(
      {
        file: selectedFile,
        addedBy,
        merchantId,
      },
      {
        onSuccess: (response) => {
          // If jobId exists, it's a background job (>50 records)
          if (response?.jobId) {
            setUploadJobId(response.jobId);
          } else {
            // Synchronous processing completed
            setTimeout(() => {
              onClose();
            }, 2000);
          }
        },
        onError: (error: any) => {
          // Error is already handled in the mutation hook, but we can log it here for debugging
          console.error('Bulk upload error details:', error);
        },
      }
    );
  };

  // Handle template download
  const handleDownloadTemplate = () => {
    downloadTemplateMutation.mutate();
  };

  // Determine current status
  const getStatusInfo = () => {
    if (uploadJobId && statusData) {
      return {
        status: statusData.status,
        progress: statusData.progress || 0,
        totalRows: statusData.totalRows || 0,
        processedRows: statusData.processedRows || 0,
        successfulRows: statusData.successfulRows || 0,
        failedRows: statusData.failedRows || 0,
        errors: statusData.errors || [],
        result: statusData.result,
      };
    }
    return null;
  };

  const statusInfo = getStatusInfo();
  const status = statusInfo?.status?.toLowerCase();
  const isProcessing = status === 'queued' || status === 'processing';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';

  return (
    <Box sx={{ p: 3, minWidth: 500 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        {t('storeItem.bulkUpload.title')}
      </Typography>

      {/* Instructions */}
      <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('storeItem.bulkUpload.instructions')}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          {t('storeItem.bulkUpload.fileSizeLimit')}
        </Typography>
      </Alert>

      {/* Template Format Help */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t('storeItem.bulkUpload.templateFormatGuide')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('storeItem.bulkUpload.templateFormatDescription')}
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                {t('storeItem.bulkUpload.requiredFields')}:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {['itemCode', 'itemName_en', 'itemName_ar', 'itemContent_en', 'itemContent_ar', 
                  'itemQuantity', 'originalPrice', 'currencyCode', 'itemCategoryId', 
                  'restaurantId', 'itemSubCategoryId'].map((field) => (
                  <Chip key={field} label={field} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                {t('storeItem.bulkUpload.importantNotes')}:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <li>
                  <Typography variant="caption">
                    {t('storeItem.bulkUpload.noteCategoryIds')}
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption">
                    {t('storeItem.bulkUpload.noteStoreIds')}
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption">
                    {t('storeItem.bulkUpload.noteMultipleImages')}
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption">
                    {t('storeItem.bulkUpload.noteBooleanFields')}
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption">
                    {t('storeItem.bulkUpload.noteAvailableTime')}
                  </Typography>
                </li>
              </Box>
            </Box>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="caption">
                {t('storeItem.bulkUpload.validationWarning')}
              </Typography>
            </Alert>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Download Template Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {t('storeItem.bulkUpload.downloadTemplate')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            disabled={downloadTemplateMutation.isPending}
          >
            {t('storeItem.bulkUpload.downloadExcel')}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* File Upload Section with Drag & Drop */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {t('storeItem.bulkUpload.selectFile')}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.csv"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id="bulk-upload-file-input"
        />
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: '1px dashed',
            borderColor: isDragOver ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 2,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: isDragOver ? 'action.hover' : 'background.paper',
            transition: 'background-color 0.2s, border-color 0.2s',
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <label htmlFor="bulk-upload-file-input" style={{ display: 'block' }}>
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              sx={{ mb: 1 }}
              fullWidth
            >
              {t('storeItem.bulkUpload.chooseFile')}
            </Button>
          </label>
          <Typography variant="caption" color="text.secondary" display="block">
          {t('storeItem.bulkUpload.message')}
          </Typography>
          {selectedFile && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('storeItem.bulkUpload.selectedFile')}: {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('storeItem.bulkUpload.fileSize')}: {(selectedFile.size / 1024).toFixed(2)} KB
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Upload Button */}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleUpload}
        disabled={!selectedFile || bulkUploadMutation.isPending || isProcessing}
        sx={{ mb: 3 }}
      >
        {bulkUploadMutation.isPending
          ? t('storeItem.bulkUpload.uploading')
          : t('storeItem.bulkUpload.upload')}
      </Button>

      {/* Error Display */}
      {bulkUploadMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {/* Show summary message based on number of errors */}
          {bulkUploadMutation.error?.response?.data?.errors && 
           Array.isArray(bulkUploadMutation.error.response.data.errors) && 
           bulkUploadMutation.error.response.data.errors.length > 0 ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {bulkUploadMutation.error.response.data.errors.length === 1 
                  ? `Validation Error (${bulkUploadMutation.error.response.data.errors.length} error found):`
                  : `Validation Errors (${bulkUploadMutation.error.response.data.errors.length} errors found):`}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {bulkUploadMutation.error?.response?.data?.error || 
               bulkUploadMutation.error?.response?.data?.message || 
               t('storeItem.bulkUpload.uploadFailed')}
            </Typography>
          )}
          
          {/* Display all validation errors */}
          {bulkUploadMutation.error?.response?.data?.errors && 
           Array.isArray(bulkUploadMutation.error.response.data.errors) && 
           bulkUploadMutation.error.response.data.errors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box 
                sx={{ 
                  maxHeight: 300, 
                  overflowY: 'auto', 
                  bgcolor: 'background.default', 
                  p: 1.5, 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                {bulkUploadMutation.error.response.data.errors.map((error: any, index: number) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      mb: index < bulkUploadMutation.error.response.data.errors.length - 1 ? 1.5 : 0, 
                      pb: index < bulkUploadMutation.error.response.data.errors.length - 1 ? 1.5 : 0, 
                      borderBottom: index < bulkUploadMutation.error.response.data.errors.length - 1 ? '1px solid' : 'none', 
                      borderColor: 'divider' 
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <ErrorIcon sx={{ fontSize: 18, color: 'error.main', mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'error.main' }}>
                          Row {error.row || 'N/A'} - {error.field || 'Field'}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            display: 'block', 
                            color: 'error.main',
                            fontWeight: 500,
                            wordBreak: 'break-word',
                            fontSize: '0.875rem',
                            mt: 0.5
                          }}
                        >
                          {error.message || 'Validation error'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          
          {/* Fallback to simple error message if no detailed errors */}
          {(!bulkUploadMutation.error?.response?.data?.errors || 
            !Array.isArray(bulkUploadMutation.error.response.data.errors) ||
            bulkUploadMutation.error.response.data.errors.length === 0) && (
            <Typography variant="caption" color="text.secondary">
              {bulkUploadMutation.error?.message || t('storeItem.bulkUpload.checkFileAndTryAgain')}
            </Typography>
          )}
        </Alert>
      )}

      {/* Status Display */}
      {uploadJobId && statusInfo && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {t('storeItem.bulkUpload.uploadStatus')}
          </Typography>

          {/* Progress Bar */}
          {isProcessing && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  {t('storeItem.bulkUpload.processing')}...
                </Typography>
                <Typography variant="body2">
                  {statusInfo.progress}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={statusInfo.progress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {statusInfo.processedRows} / {statusInfo.totalRows} {t('storeItem.bulkUpload.rowsProcessed')}
              </Typography>
            </Box>
          )}

          {/* Completed Status */}
          {isCompleted && (
            <Alert
              icon={<CheckCircleIcon />}
              severity="success"
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('storeItem.bulkUpload.completed')}
              </Typography>
              {statusInfo.result && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {statusInfo.result.message || 
                    `${t('storeItem.bulkUpload.itemsCreated')}: ${statusInfo.result.itemsCreated || 0}`}
                </Typography>
              )}
            </Alert>
          )}

          {/* Failed Status */}
          {isFailed && (
            <Alert
              icon={<ErrorIcon />}
              severity="error"
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('storeItem.bulkUpload.failed')}
              </Typography>
              {statusInfo.errors && statusInfo.errors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {t('storeItem.bulkUpload.errors')}:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                    {statusInfo.errors.slice(0, 5).map((error, index) => (
                      <li key={index}>
                        <Typography variant="caption">
                          {error.message || `${t('storeItem.bulkUpload.row')} ${error.row}: ${error.field}`}
                        </Typography>
                      </li>
                    ))}
                    {statusInfo.errors.length > 5 && (
                      <Typography variant="caption" color="text.secondary">
                        ...{statusInfo.errors.length - 5} {t('storeItem.bulkUpload.moreErrors')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Alert>
          )}

          {/* Statistics */}
          {statusInfo && (isCompleted || isFailed) && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                {t('storeItem.bulkUpload.statistics')}:
              </Typography>
              <Typography variant="caption" display="block">
                {t('storeItem.bulkUpload.totalRows')}: {statusInfo.totalRows}
              </Typography>
              <Typography variant="caption" display="block" color="success.main">
                {t('storeItem.bulkUpload.successfulRows')}: {statusInfo.successfulRows || 0}
              </Typography>
              {statusInfo.failedRows && statusInfo.failedRows > 0 && (
                <Typography variant="caption" display="block" color="error.main">
                  {t('storeItem.bulkUpload.failedRows')}: {statusInfo.failedRows}
                </Typography>
              )}
            </Box>
          )}

          {/* Close button when completed or failed */}
          {(isCompleted || isFailed) && (
            <Button
              variant="contained"
              fullWidth
              onClick={onClose}
              sx={{ mt: 2 }}
            >
              {t('storeItem.bulkUpload.close')}
            </Button>
          )}
        </Box>
      )}

      {/* Loading indicator */}
      {isStatusLoading && !statusInfo && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

export default BulkUploadModal;

